/**
 * Form field and input components for admin and composer UIs.
 *
 * `<FormField>` is the standard labelled wrapper: it renders a label, optional
 * required marker, optional accessory action slot, and error/description text
 * in the appropriate colour. Build all labelled form controls on top of it.
 * `<DropdownField>` delegates to `<Popup>` for its flyout panel.
 * `<ListSelector>` combines an add-widget with reorderable item management
 * (up/down/delete) and is used in admin dashboards for configuring ordered
 * lists. `<ToggleSwitch>` is re-exported from the `form/` subfolder so
 * callers have a single import point.
 */

import type { CarbonIconType } from '@carbon/icons-react';
import { Add, ArrowDown, ArrowUp, ChevronDown, RadioButton, RadioButtonChecked, Search, TrashCan, Undo } from '@carbon/icons-react';
import { toHoverColor, toPressColor, useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { getFontLineHeight, getFontSize } from 'platform-specific/font';
import React, { type ComponentType, useState } from 'react';
import { View } from 'react-native';
import type { Direction, FormatParams, TLabel } from 'util/stdtypes';
import { makeTestID, t } from 'util/stdtypes';

import { HorizBox, HoverView, Pad, PadBox, SeparatedBox } from './basics';
import { ActionButton, CTAButton, SubtleButton } from './button';
import { useFormDisabled } from './form/form-disabled';
import { ToggleSwitch } from './form/toggle-switch';
import { Popup } from './popup';
import type { TextFieldProps } from './text';
import { Heading, TextField, UtilityText } from './text';
import { useTranslation } from './translation';
import { TextButton } from './ui/text-button';

export { ToggleSwitch };

export type FormFieldProps = {
    label?: TLabel;
    formatParams?: FormatParams;
    text?: string;
    descriptionLabel?: TLabel;
    descriptionText?: string;
    errorLabel?: TLabel;
    errorText?: string;
    required?: boolean;
    accessoryAction?: React.ReactElement;
    children: React.ReactNode;
};

export function FormField({
    label,
    formatParams,
    text,
    descriptionLabel,
    descriptionText,
    errorLabel,
    errorText,
    required = false,
    accessoryAction,
    children,
}: FormFieldProps) {
    const { colors } = useTheme();
    const tLabel = useTranslation(label, formatParams);
    return (
        <View>
            <HorizBox center>
                {required && (
                    <UtilityText
                        formatParams={{ label: tLabel }}
                        label={label ? t('form.required_label', '{label} (required)') : undefined}
                        text={text ? text + ' (required)' : undefined}
                        type='small'
                        weight='medium'
                    />
                )}
                {!required && <UtilityText formatParams={formatParams} label={label} text={text} type='small' weight='medium' />}
                {accessoryAction && <View style={{ marginLeft: 'auto' }}>{accessoryAction}</View>}
            </HorizBox>
            <Pad size={8} />
            {children}
            {(errorLabel || errorText) && (
                <PadBox top={8}>
                    <UtilityText color={colors.colorTextWarning} label={errorLabel} text={errorText} type='small' />
                </PadBox>
            )}
            {(descriptionLabel || descriptionText) && (
                <PadBox top={8}>
                    <UtilityText color={colors.colorTextSecondary} label={descriptionLabel} text={descriptionText} type='small' />
                </PadBox>
            )}
        </View>
    );
}

type RadioContextType = { radioSelection: string | null; onSetRadio?: (value: string) => void };

const RadioContext = React.createContext<RadioContextType>({ radioSelection: null });

export type RadioGroupProps = { children: React.ReactNode; value: string; onChange: (value: string) => void };

export function RadioGroup({ children, value, onChange }: RadioGroupProps) {
    const [radioSelection, setRadioSelection] = React.useState(value);

    const onSetRadio = React.useCallback(
        (value: string) => {
            setRadioSelection(value);
            onChange?.(value);
        },
        [onChange],
    );

    const contextValue = React.useMemo(() => ({ radioSelection, onSetRadio }), [radioSelection, onSetRadio]);

    return (
        <RadioContext.Provider value={contextValue}>
            <View role='radiogroup'>{children}</View>
        </RadioContext.Provider>
    );
}

export type RadioOptionProps = { emoji?: string; text?: string; radioKey: string; label?: TLabel; disabled?: boolean };

export function RadioOption({ emoji, text, radioKey, label, disabled: ownDisabled }: RadioOptionProps) {
    const formDisabled = useFormDisabled();
    const disabled = formDisabled || ownDisabled;
    const { colors } = useTheme();
    const { radioSelection, onSetRadio } = React.useContext(RadioContext);
    const [hover, setHover] = useState(false);
    const value = radioSelection === radioKey;
    const disabledStyle = { opacity: 0.6 };
    return (
        <HoverView
            aria-checked={!!value}
            disabled={disabled}
            onHoverIn={() => setHover(true)}
            onHoverOut={() => setHover(false)}
            onPress={() => onSetRadio?.(radioKey)}
            role='radio'
            style={disabled ? disabledStyle : null}
            testID={makeTestID({ label, text })}
        >
            <PadBox vert={8}>
                <HorizBox center>
                    {value ? (
                        <RadioButtonChecked color={colors.colorButtonPrimary} size={32} />
                    ) : (
                        <RadioButton size={32} style={{ fill: hover ? colors.colorBorderSelected : colors.colorBorderPrimary }} />
                    )}
                    <Pad size={12} />
                    {emoji && (
                        <PadBox right={6}>
                            <UtilityText aria-hidden text={emoji} type='tiny' weight='strong' />
                        </PadBox>
                    )}
                    <UtilityText label={label} text={text} />
                </HorizBox>
            </PadBox>
        </HoverView>
    );
}

export type SearchFieldProps = TextFieldProps;

export function SearchField(props: SearchFieldProps) {
    return <TextField icon={Search} multiline={false} {...props} />;
}

type DropdownOptionProps = {
    option: DropdownFieldOption;
    onPress: () => void;
};

function DropdownOption({ option, onPress }: DropdownOptionProps) {
    const s = useDropdownOptionStyles();
    return (
        <HoverView hoverStyle={s.hover} onPress={onPress} pressedStyle={s.pressed} style={s.option} testID={option.key}>
            <UtilityText label={option.label} text={option.text} />
        </HoverView>
    );
}
const useDropdownOptionStyles = createThemedStyles((tokens) => ({
    option: {
        height: 48,
        paddingHorizontal: 20,
        justifyContent: 'center',
    },
    hover: {
        backgroundColor: toHoverColor(tokens.colors.colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: toPressColor(tokens.colors.colorSurfacePrimary),
    },
}));

export type DropdownFieldOption = {
    key: string;
    label?: TLabel;
    text?: string;
};

type DropdownFieldProps = {
    placeholder: TLabel;
    value: string | null;
    disabled?: boolean;
    onChange: (value: string) => Promise<void> | void;
    options: DropdownFieldOption[];
    testID?: string;
};

export function DropdownField({ placeholder, options, value, onChange, testID, disabled: ownDisabled = false }: DropdownFieldProps) {
    const formDisabled = useFormDisabled();
    const disabled = formDisabled || ownDisabled;
    const { colors } = useTheme();
    const s = useDropdownFieldStyles();
    const [hover, setHover] = useState(false);
    function popupContent({ onClose }: { onClose: () => void }) {
        return (
            <View>
                {options.map((option) => (
                    <DropdownOption
                        key={option.key}
                        onPress={async () => {
                            await onChange(option.key);
                            onClose();
                        }}
                        option={option}
                    />
                ))}
            </View>
        );
    }
    const selectedOption = options.find((option) => option.key === value);

    const displayedSelectedOption = selectedOption ? (
        <UtilityText color={colors.colorTextPrimary} label={selectedOption.label} text={selectedOption.text} type='large' />
    ) : (
        <UtilityText color={colors.colorTextDisabled} label={placeholder} type='large' />
    );

    if (disabled) {
        return (
            // Matches the enabled field's popup wrapper so both lay out alike.
            <View>
                <View aria-disabled={true} role='button' style={[s.dropdownField, s.disabled]} testID={testID}>
                    {displayedSelectedOption}
                    <ChevronDown size={24} />
                </View>
            </View>
        );
    }

    return (
        <Popup matchWidth popupContent={popupContent} popupStyle={s.popup} setHover={setHover}>
            <View role='button' style={[s.dropdownField, hover && s.hover]} testID={testID}>
                {displayedSelectedOption}
                <ChevronDown size={24} />
            </View>
        </Popup>
    );
}

const useDropdownFieldStyles = createThemedStyles((tokens) => ({
    popup: {
        padding: 0,
        borderRadius: 0,
    },
    dropdownFieldText: {
        color: tokens.colors.colorTextPrimary,
        fontFamily: tokens.fonts.fontFamilySansMedium,
        fontSize: getFontSize(16),
    },
    dropdownField: {
        height: 48,
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'stretch',
        justifyContent: 'space-between',
        borderColor: tokens.colors.colorBorderPrimary,
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderWidth: 1,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 15,
        fontSize: getFontSize(16),
        lineHeight: getFontLineHeight(16, 1.5),
        fontFamily: tokens.fonts.fontFamilySansRegular,
        color: tokens.colors.colorTextPrimary,
    },
    disabled: {
        backgroundColor: tokens.colors.colorButtonDisabled,
        borderColor: tokens.colors.colorButtonDisabled,
        color: tokens.colors.colorTextDisabled,
    },
    hover: {
        borderColor: toHoverColor(tokens.colors.colorBorderSelected),
    },
}));

export type PlaceholderContainerProps = { children: React.ReactNode };

export function PlaceholderContainer({ children }: PlaceholderContainerProps) {
    const s = usePlaceholderContainerStyles();
    return <View style={s.container}>{children}</View>;
}

const usePlaceholderContainerStyles = createThemedStyles((tokens) => ({
    container: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderColor: tokens.colors.colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 10,
        padding: 16,
        borderStyle: 'dashed',
    },
}));

export type EditingCardProps = { children: React.ReactNode };

export function EditingCard({ children }: EditingCardProps) {
    const s = useEditingCardStyles();
    return <View style={s.container}>{children}</View>;
}

const useEditingCardStyles = createThemedStyles((tokens) => ({
    container: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderColor: tokens.colors.colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 10,
        padding: 20,
    },
}));

type ListItem = { key: string; text: string };

type ListSelectorVariantType = 'singleSelect' | 'multiSelect';

type ListSelectorProps<T> = {
    label: TLabel;
    addLabel: TLabel;
    addHintLabel?: TLabel;
    items: ListItem[];
    addWidget: ComponentType<{ setToAdd: (items: T[]) => void; onSubmit: () => void }>;
    reorderable: boolean;
    onDeleteItem: (key: string) => void;
    onAddItems: (items: T[]) => Promise<{ error: TLabel | null } | void>;
    onChangeSortOrder: (orderedKeys: string[]) => void;
    deleteIcon?: CarbonIconType;
    variant?: ListSelectorVariantType;
};

export function ListSelector<T>({
    label,
    addLabel,
    addHintLabel,
    items,
    addWidget,
    reorderable,
    onDeleteItem,
    onAddItems,
    onChangeSortOrder,
    deleteIcon,
    variant,
}: ListSelectorProps<T>) {
    const [adding, setAdding] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const [toAdd, setToAdd] = useState([] as T[]);
    const [error, setError] = useState<TLabel | null>(null);
    const [resetKey, setResetKey] = useState(0);

    function handleUncheckAll() {
        setToAdd([]);
        setResetKey((k) => k + 1);
    }

    function handleCancel() {
        setAdding(false);
        setToAdd([]);
        setError(null);
    }

    async function onAdd() {
        setInProgress(true);
        setError(null);
        const result = await onAddItems(toAdd);
        setInProgress(false);
        if (result && result.error) {
            setError(result.error);
        } else {
            setToAdd([]);
            setAdding(false);
        }
    }

    if (adding) {
        return (
            <ListSelectorAdding
                addLabel={addLabel}
                addWidget={addWidget}
                error={error}
                inProgress={inProgress}
                label={label}
                onAdd={onAdd}
                onCancel={handleCancel}
                onUncheckAll={handleUncheckAll}
                resetKey={resetKey}
                setToAdd={setToAdd}
                toAdd={toAdd}
                variant={variant}
            />
        );
    } else {
        return (
            <ListSelectorIdle
                addHintLabel={addHintLabel}
                addLabel={addLabel}
                deleteIcon={deleteIcon}
                items={items}
                label={label}
                onChangeSortOrder={onChangeSortOrder}
                onDeleteItem={onDeleteItem}
                onStartAdding={() => setAdding(true)}
                reorderable={reorderable}
            />
        );
    }
}

type ListSelectorAddingProps<T> = {
    addLabel: TLabel;
    label: TLabel;
    variant?: ListSelectorVariantType;
    toAdd: T[];
    setToAdd: (items: T[]) => void;
    resetKey: number;
    inProgress: boolean;
    error: TLabel | null;
    addWidget: ComponentType<{ setToAdd: (items: T[]) => void; onSubmit: () => void }>;
    onAdd: () => void;
    onCancel: () => void;
    onUncheckAll: () => void;
};

function ListSelectorAdding<T>({
    addLabel,
    label,
    variant,
    toAdd,
    setToAdd,
    resetKey,
    inProgress,
    error,
    addWidget,
    onAdd,
    onCancel,
    onUncheckAll,
}: ListSelectorAddingProps<T>) {
    const { colors } = useTheme();
    const cancelLabel = t('form.cancel_button', 'Cancel');
    const cancelText = useTranslation(cancelLabel);
    const AddWidget = addWidget;
    return (
        <FormField label={label}>
            <EditingCard>
                <HorizBox center spread>
                    <HorizBox center>
                        <Heading label={addLabel} type='small' weight='strong' />
                        {variant === 'multiSelect' && toAdd.length > 0 && (
                            <PadBox left={8}>
                                <CountPill count={toAdd.length} />
                            </PadBox>
                        )}
                    </HorizBox>
                    {variant === 'multiSelect' && toAdd.length > 0 && (
                        <SubtleButton icon={Undo} label={t('form.uncheck_all', 'Uncheck all')} onPress={onUncheckAll} />
                    )}
                </HorizBox>
                <Pad size={24} />
                <AddWidget key={resetKey} onSubmit={onAdd} setToAdd={setToAdd} />
                {error && (
                    <>
                        <Pad size={16} />
                        <UtilityText color={colors.colorTextWarning} label={error} type='small' />
                    </>
                )}
                <Pad size={24} />
                <HorizBox center right>
                    <TextButton onPress={onCancel} testID={cancelLabel.defaultText} text={cancelText} />
                    <Pad size={16} />
                    <CTAButton disabled={toAdd.length === 0} inProgress={inProgress} label={t('form.add_button', 'Add')} onPress={onAdd} />
                </HorizBox>
            </EditingCard>
        </FormField>
    );
}

type ListSelectorIdleProps = {
    label: TLabel;
    addLabel: TLabel;
    addHintLabel?: TLabel;
    items: ListItem[];
    reorderable: boolean;
    onDeleteItem: (key: string) => void;
    onChangeSortOrder: (orderedKeys: string[]) => void;
    onStartAdding: () => void;
    deleteIcon?: CarbonIconType;
};

function ListSelectorIdle({
    label,
    addLabel,
    addHintLabel,
    items,
    reorderable,
    onDeleteItem,
    onChangeSortOrder,
    onStartAdding,
    deleteIcon,
}: ListSelectorIdleProps) {
    const { colors } = useTheme();
    return (
        <FormField label={label}>
            <Pad size={4} />
            <PlaceholderContainer>
                <SubtleButton color={colors.colorTextPrimary} icon={Add} label={addLabel} onPress={onStartAdding} />
            </PlaceholderContainer>
            {addHintLabel && (
                <PadBox vert={12}>
                    <UtilityText color={colors.colorTextSecondary} label={addHintLabel} type='small' />
                </PadBox>
            )}
            <ListSelectorItemList
                deleteIcon={deleteIcon}
                items={items}
                onChangeSortOrder={onChangeSortOrder}
                onDeleteItem={onDeleteItem}
                reorderable={reorderable}
            />
        </FormField>
    );
}

function CountPill({ count }: { count: number }) {
    const { colors } = useTheme();
    const s = useCountPillStyles();
    return (
        <View style={s.pill}>
            <UtilityText color={colors.colorTextPrimary} text={String(count)} type='small' weight='medium' />
        </View>
    );
}

const useCountPillStyles = createThemedStyles((tokens) => ({
    pill: {
        backgroundColor: tokens.colors.colorButtonDisabled,
        borderRadius: 100,
        minHeight: 20,
        minWidth: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

type ListSelectorItemListProps = {
    items: ListItem[];
    onChangeSortOrder: (orderedKeys: string[]) => void;
    onDeleteItem: (key: string) => void;
    reorderable: boolean;
    deleteIcon?: CarbonIconType;
};

function ListSelectorItemList({ items, onChangeSortOrder, onDeleteItem, reorderable, deleteIcon }: ListSelectorItemListProps) {
    return (
        <SeparatedBox>
            {items.map((item, idx, collection) => {
                const isTopItem = idx === 0;
                const isBottomItem = idx === collection.length - 1;

                function handleChangeSortOrder(direction: Direction) {
                    if (!onChangeSortOrder || !reorderable) return;
                    const oldOrder = collection.map((item) => item.key);
                    const newOrder = reorderList(direction, oldOrder, idx);
                    onChangeSortOrder(newOrder);
                }

                return (
                    <ListSelectorItem
                        deleteIcon={deleteIcon}
                        disableOrderDown={isBottomItem}
                        disableOrderUp={isTopItem}
                        item={item}
                        key={item.key}
                        onChangeSortOrder={handleChangeSortOrder}
                        onDeleteItem={onDeleteItem}
                        reorderable={reorderable}
                    />
                );
            })}
        </SeparatedBox>
    );
}

function reorderList(direction: Direction, oldOrder: string[], idx: number) {
    const neighbor = direction === 'up' ? oldOrder[idx - 1] : oldOrder[idx + 1];
    if (!neighbor) return oldOrder;
    const newOrder = [...oldOrder];
    const currentIdx = newOrder.indexOf(oldOrder[idx]);
    const neighborIdx = newOrder.indexOf(neighbor);
    const tmp = newOrder[currentIdx];
    newOrder[currentIdx] = newOrder[neighborIdx];
    newOrder[neighborIdx] = tmp;
    return newOrder;
}

type ListSelectorItemProps = {
    item: { key: string; text: string; order?: number };
    onChangeSortOrder?: (direction: Direction) => void;
    onDeleteItem: (key: string) => void;
    reorderable?: boolean;
    disableOrderUp?: boolean;
    disableOrderDown?: boolean;
    deleteIcon?: CarbonIconType;
};

function ListSelectorItem({
    item,
    reorderable,
    onDeleteItem,
    onChangeSortOrder,
    disableOrderUp,
    disableOrderDown,
    deleteIcon,
}: ListSelectorItemProps) {
    const { colors } = useTheme();
    function handleChangeSortOrder(direction: Direction) {
        return () => {
            if (onChangeSortOrder) onChangeSortOrder(direction);
        };
    }

    return (
        <View style={{ height: 48, justifyContent: 'center', display: 'flex' }}>
            <HorizBox center spread testID={item.text}>
                <UtilityText numberOfLines={1} text={item.text} />

                <Pad size={8} />

                <HorizBox gap={8}>
                    <ActionButton
                        icon={deleteIcon ?? TrashCan}
                        iconProps={{ color: colors.colorTextPrimary }}
                        onPress={() => onDeleteItem(item.key)}
                        testID='delete'
                    />
                    {reorderable && (
                        <>
                            <ActionButton
                                disabled={disableOrderUp}
                                icon={ArrowUp}
                                iconProps={{ color: colors.colorTextPrimary }}
                                onPress={handleChangeSortOrder('up')}
                                testID='up'
                            />
                            <ActionButton
                                disabled={disableOrderDown}
                                icon={ArrowDown}
                                iconProps={{ color: colors.colorTextPrimary }}
                                onPress={handleChangeSortOrder('down')}
                                testID='down'
                            />
                        </>
                    )}
                </HorizBox>
            </HorizBox>
        </View>
    );
}
